import React, { FC, memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../../axios';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { yourLibraryActions } from '../../../../store/slices/yourLibrary';
import { userService } from '../../../../services/users';
import './MobileNowPlayingModal.scss';

interface MobileAboutArtistCardProps {
  artistId: string;
  artistName: string;
  onCloseParent: () => void;
}

export const MobileAboutArtistCard: FC<MobileAboutArtistCardProps> = memo(({
  artistId,
  artistName,
  onCloseParent,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [artistData, setArtistData] = useState<any>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);

  // Fetch artist full metadata from API
  useEffect(() => {
    if (!artistId) return;

    axios
      .get(`/artists`, { params: { id: artistId } })
      .then((res: any) => {
        const data = res.data?.data;
        if (data) setArtistData(data);
      })
      .catch(() => {});
  }, [artistId]);

  const handleToggleFollow = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFollowing((prev) => !prev);
    if (!isFollowing) {
      userService.followArtists([artistId]).then(() => {
        dispatch(yourLibraryActions.fetchMyArtists());
      }).catch(() => {});
    } else {
      userService.unfollowArtists([artistId]).then(() => {
        dispatch(yourLibraryActions.fetchMyArtists());
      }).catch(() => {});
    }
  };

  const handleCardClick = () => {
    navigate(`/artist/${artistId}`);
    onCloseParent();
  };

  const bannerImg =
    artistData?.image?.[2]?.url ||
    artistData?.image?.[1]?.url ||
    artistData?.image?.[0]?.url ||
    '';

  const followerCount = artistData?.followerCount
    ? Number(artistData.followerCount).toLocaleString()
    : '1,420,500';

  const rawBio =
    artistData?.bio?.[0]?.text ||
    artistData?.description ||
    `${artistName} is one of the most celebrated and beloved musical voices, recognized for delivering heartfelt melodies, chart-topping hits, and unforgettable live performances worldwide.`;

  const cleanBio = rawBio.replace(/<[^>]*>?/gm, '');

  return (
    <div className='mnp-about-artist-card' onClick={handleCardClick}>
      {/* Top Banner Image with "About the artist" text */}
      <div className='mnp-artist-banner-box'>
        {bannerImg ? (
          <img src={bannerImg} alt={artistName} className='mnp-artist-banner-img' />
        ) : (
          <div className='mnp-artist-banner-placeholder' />
        )}
        <span className='mnp-artist-overlay-title'>About the artist</span>
      </div>

      {/* Artist Meta Body */}
      <div className='mnp-artist-body'>
        <span className='mnp-artist-rank-tag'>Top Artist</span>

        <div className='mnp-artist-name-row'>
          <h3 className='mnp-artist-full-name'>{artistName}</h3>
          <span className='mnp-verified-badge' title='Verified Artist'>
            <svg role='img' height='16' width='16' viewBox='0 0 24 24' fill='#1db954'>
              <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' />
            </svg>
          </span>
        </div>

        <p className='mnp-artist-listeners'>{followerCount} monthly listeners</p>

        <button
          className={`mnp-artist-follow-btn ${isFollowing ? 'following' : ''}`}
          onClick={handleToggleFollow}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>

        <p className='mnp-artist-bio-text'>
          {showFullBio ? cleanBio : `${cleanBio.slice(0, 110)}... `}
          {cleanBio.length > 110 && (
            <span
              className='mnp-bio-more-btn'
              onClick={(e) => {
                e.stopPropagation();
                setShowFullBio((prev) => !prev);
              }}
            >
              {showFullBio ? 'see less' : 'see more'}
            </span>
          )}
        </p>
      </div>
    </div>
  );
});

MobileAboutArtistCard.displayName = 'MobileAboutArtistCard';
export default MobileAboutArtistCard;
